import React from 'react';

export type ErrorSeverity = 'error' | 'warning' | 'info';

interface ErrorMessageProps {
  title: string;
  message?: string;
  severity?: ErrorSeverity;
  className?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  retryLabel?: string;
  dismissLabel?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

export function ErrorMessage({
  title,
  message,
  severity = 'error',
  className = '',
  onRetry,
  onDismiss,
  retryLabel = 'Try Again',
  dismissLabel = 'Dismiss',
  icon,
  children
}: ErrorMessageProps) {
  const severityStyles = {
    error: {
      container: 'bg-red-50 border-red-200 text-red-800',
      icon: 'text-red-500',
      button: 'text-red-600 hover:text-red-800'
    },
    warning: {
      container: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      icon: 'text-yellow-500',
      button: 'text-yellow-600 hover:text-yellow-800'
    },
    info: {
      container: 'bg-blue-50 border-blue-200 text-blue-800',
      icon: 'text-blue-500',
      button: 'text-blue-600 hover:text-blue-800'
    }
  };

  const styles = severityStyles[severity];

  const getDefaultIcon = () => {
    switch (severity) {
      case 'error':
        return (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
        return (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`border-2 rounded-lg p-4 ${styles.container} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        {/* Icon */}
        <div className={`mr-3 flex-shrink-0 mt-0.5 ${styles.icon}`}>
          {icon || getDefaultIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-1">
            {title}
          </h3>
          
          {message && (
            <p className="text-sm mb-3">
              {message}
            </p>
          )}

          {children && (
            <div className="mb-3">
              {children}
            </div>
          )}

          {/* Action Buttons */}
          {(onRetry || onDismiss) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className={`text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current rounded ${styles.button}`}
                >
                  {retryLabel}
                </button>
              )}
              
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className={`text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current rounded ${styles.button}`}
                >
                  {dismissLabel}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Close Button */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`ml-3 flex-shrink-0 p-1 rounded-full hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current transition-colors duration-200 ${styles.icon}`}
            aria-label="Close error message"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

interface NetworkErrorMessageProps {
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export function NetworkErrorMessage({ onRetry, onDismiss, className }: NetworkErrorMessageProps) {
  return (
    <ErrorMessage
      title="Network Connection Error"
      message="Unable to connect to the server. Please check your internet connection and try again."
      severity="error"
      onRetry={onRetry}
      onDismiss={onDismiss}
      retryLabel="Retry Connection"
      className={className}
      icon={
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    />
  );
}

interface ValidationErrorMessageProps {
  errors: Record<string, string>;
  onDismiss?: () => void;
  className?: string;
}

export function ValidationErrorMessage({ errors, onDismiss, className }: ValidationErrorMessageProps) {
  const errorCount = Object.keys(errors).length;
  
  if (errorCount === 0) return null;

  return (
    <ErrorMessage
      title={`Please correct ${errorCount} error${errorCount > 1 ? 's' : ''}`}
      severity="warning"
      onDismiss={onDismiss}
      className={className}
    >
      <ul className="list-disc list-inside space-y-1 text-sm">
        {Object.entries(errors).map(([field, message]) => (
          <li key={field}>
            <span className="font-medium capitalize">{field}:</span> {message}
          </li>
        ))}
      </ul>
    </ErrorMessage>
  );
}